// Temporary importer for the snapshots written by the file:// bridge release.
// Normal app persistence continues to use Redux Persist's localStorage adapter.
export const ORIGIN_MIGRATION_MARKER = 'vv_native_origin_migration_v1';
export const ORIGIN_MIGRATION_FILES = [
  'vv-origin-migration-redux-v1-a.json',
  'vv-origin-migration-redux-v1-b.json',
];
export const ORIGIN_MIGRATION_TIMEOUT_MS = 2000;
const PERSISTED_REDUX_KEY = 'persist:root';

export type OriginMigrationOutcome =
  'imported' | 'signed-out' | 'missing' | 'invalid' | 'unavailable' |
  'read-failed' | 'timed-out' | 'storage-failed' | 'skipped';

interface IOriginSnapshot {
  version: number;
  savedAt: number;
  platformId: string;
  sourceProtocol: string;
  payload: string | null;
  payloadLength: number;
  checksum: string;
}

interface IBackupFile {
  file: (success: (file: File) => void, failure: (error: unknown) => void) => void;
  remove: (success: () => void, failure: (error: unknown) => void) => void;
}

interface IBackupDirectory {
  getFile: (
    name: string,
    options: { create: boolean },
    success: (file: IBackupFile) => void,
    failure: (error: unknown) => void
  ) => void;
}

interface IBackupRead {
  snapshot?: IOriginSnapshot;
  entry?: IBackupFile;
  outcome: 'valid' | 'missing' | 'invalid' | 'read-failed';
}

declare const window: any;

function isNewNativeOrigin(): boolean {
  const platformId = window.cordova?.platformId;
  return window.location.hostname === 'localhost' && (
    (platformId === 'android' && window.location.protocol === 'https:') ||
    (platformId === 'ios' && window.location.protocol === 'app:')
  );
}

function markChecked(outcome: string): boolean {
  try {
    window.localStorage.setItem(ORIGIN_MIGRATION_MARKER, outcome);
    return true;
  } catch {
    console.warn('Could not save the origin migration marker.');
    return false;
  }
}

/**
 * Synchronous fast path. Existing localhost state is authoritative, including
 * Android state preseeded by the earlier iframe bridge. Do not touch native
 * files in this case. Mark it checked so a later logout cannot revive a backup.
 */
export function needsNativeOriginStorageMigration(): boolean {
  if (!isNewNativeOrigin()) {
    return false;
  }
  try {
    if (window.localStorage.getItem(ORIGIN_MIGRATION_MARKER) !== null) {
      return false;
    }
    if (window.localStorage.getItem(PERSISTED_REDUX_KEY) !== null) {
      markChecked('existing-state');
      return false;
    }
    return true;
  } catch {
    // If the destination cannot be inspected, never overwrite it from a backup.
    return false;
  }
}

export function markNativeOriginStorageLogout(): void {
  // Retry the local marker before purging credentials if an earlier write
  // failed. This never opens a file or invokes a native plugin.
  if (isNewNativeOrigin()) {
    markChecked('signed-out');
  }
}

function checksum(payload: string): string {
  // Same FNV-1a checksum as the shipped bridge; detects truncated writes.
  /* tslint:disable:no-bitwise */
  let hash = 0x811c9dc5;
  for (let index = 0; index < payload.length; index += 1) {
    hash ^= payload.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  const result = (`00000000${(hash >>> 0).toString(16)}`).slice(-8);
  /* tslint:enable:no-bitwise */
  return result;
}

function parseSnapshot(contents: string, platformId: string): IOriginSnapshot | undefined {
  try {
    const snapshot = JSON.parse(contents) as IOriginSnapshot;
    if (!snapshot || snapshot.version !== 1 ||
        !Number.isFinite(snapshot.savedAt) || snapshot.savedAt < 0 ||
        snapshot.platformId !== platformId || snapshot.sourceProtocol !== 'file:' ||
        (snapshot.payload !== null && typeof snapshot.payload !== 'string') ||
        snapshot.payloadLength !== (snapshot.payload === null ? 0 : snapshot.payload.length) ||
        snapshot.checksum !== checksum(snapshot.payload || '')) {
      return undefined;
    }
    if (snapshot.payload !== null) {
      // Redux Persist v5 serializes the root object AND each of its slices.
      // A valid checksum alone does not establish a readable persisted state.
      const root = JSON.parse(snapshot.payload);
      if (!root || typeof root !== 'object' || Array.isArray(root)) {
        return undefined;
      }
      for (const key of Object.keys(root)) {
        if (typeof root[key] !== 'string' || key === '__proto__') {
          return undefined;
        }
        JSON.parse(root[key]);
      }
    }
    return snapshot;
  } catch {
    return undefined;
  }
}

function isMissingFile(error: any): boolean {
  // Cordova FileError.NOT_FOUND_ERR. Other errors are not fresh installs.
  return !!error && error.code === 1;
}

function readBackup(
  directory: IBackupDirectory,
  name: string,
  platformId: string,
  isActive: () => boolean
): Promise<IBackupRead> {
  return new Promise((resolve) => {
    try {
      directory.getFile(name, { create: false }, (entry) => {
        if (!isActive()) {
          resolve({ outcome: 'read-failed' });
          return;
        }
        try {
          entry.file((file) => {
            if (!isActive()) {
              resolve({ outcome: 'read-failed', entry });
              return;
            }
            try {
              const reader = new FileReader();
              reader.onerror = () => resolve({ outcome: 'read-failed', entry });
              reader.onabort = reader.onerror;
              reader.onload = () => {
                const snapshot = isActive() && typeof reader.result === 'string'
                  ? parseSnapshot(reader.result, platformId) : undefined;
                resolve({ outcome: snapshot ? 'valid' : 'invalid', snapshot, entry });
              };
              reader.readAsText(file);
            } catch {
              resolve({ outcome: 'read-failed', entry });
            }
          }, () => resolve({ outcome: 'read-failed', entry }));
        } catch {
          resolve({ outcome: 'read-failed', entry });
        }
      }, (error) => resolve({ outcome: isMissingFile(error) ? 'missing' : 'read-failed' }));
    } catch {
      resolve({ outcome: 'read-failed' });
    }
  });
}

/**
 * Call only after deviceready, before creating the store. Checks existing files
 * once; a fresh install has no files and proceeds to signup/login immediately.
 * The timeout also invalidates late callbacks, so a delayed native response
 * cannot overwrite a session after the app has continued to login.
 */
export function importNativeOriginStorage(): Promise<OriginMigrationOutcome> {
  if (!needsNativeOriginStorageMigration()) {
    return Promise.resolve('skipped');
  }
  return new Promise((resolve) => {
    let finished = false;
    const timeout = window.setTimeout(() => finish('timed-out'), ORIGIN_MIGRATION_TIMEOUT_MS);
    const finish = (outcome: OriginMigrationOutcome): boolean => {
      if (finished) {
        return false;
      }
      finished = true;
      window.clearTimeout(timeout);
      const marked = outcome === 'skipped' || markChecked(outcome);
      resolve(marked ? outcome : 'storage-failed');
      return marked;
    };

    const dataDirectory = window.cordova?.file?.dataDirectory;
    if (typeof dataDirectory !== 'string' || typeof window.resolveLocalFileSystemURL !== 'function') {
      finish('unavailable');
      return;
    }

    try {
      window.resolveLocalFileSystemURL(dataDirectory, (directory: IBackupDirectory) => {
        if (finished) {
          return;
        }
        const platformId = window.cordova.platformId;
        void Promise.all(ORIGIN_MIGRATION_FILES.map(
          name => readBackup(directory, name, platformId, () => !finished)
        )).then((copies) => {
          if (finished) {
            return;
          }
          // Recheck after asynchronous reads: another startup or a logout may
          // already have established the new origin's state/marker.
          if (!needsNativeOriginStorageMigration()) {
            finish('skipped');
            return;
          }
          const snapshot = copies.reduce<IOriginSnapshot | undefined>((newest, copy) => {
            const candidate = copy.snapshot;
            // Two writes can share a millisecond. In a tie, logout wins.
            return candidate && (!newest || candidate.savedAt > newest.savedAt ||
              (candidate.savedAt === newest.savedAt && candidate.payload === null)) ? candidate : newest;
          }, undefined);

          if (!snapshot) {
            finish(copies.some(copy => copy.outcome === 'read-failed') ? 'read-failed' :
              copies.some(copy => copy.outcome === 'invalid') ? 'invalid' : 'missing');
            return;
          }

          const outcome = snapshot.payload === null ? 'signed-out' : 'imported';
          // A null payload is the logout tombstone, never a reason to fall
          // back to an older authenticated snapshot.
          try {
            if (snapshot.payload !== null) {
              window.localStorage.setItem(PERSISTED_REDUX_KEY, snapshot.payload);
              if (window.localStorage.getItem(PERSISTED_REDUX_KEY) !== snapshot.payload) {
                throw new Error('Origin migration storage write was not retained.');
              }
            }
          } catch {
            finish('storage-failed');
            return;
          }

          if (!finish(outcome)) {
            return;
          }
          // Destination state and marker are durable before removing either
          // copy. Deletion is best effort and does not delay app startup. Even
          // if it fails, future launches never access these files again.
          copies.forEach(({ entry }) => {
            if (entry) {
              try {
                entry.remove(() => undefined, () => {
                  console.warn('Could not remove a completed origin migration backup.');
                });
              } catch {
                console.warn('Could not remove a completed origin migration backup.');
              }
            }
          });
        }).catch(() => finish('read-failed'));
      }, (error: unknown) => finish(isMissingFile(error) ? 'missing' : 'read-failed'));
    } catch {
      finish('read-failed');
    }
  });
}



// WEBPACK FOOTER //
// ./src/helpers/nativeOriginStorageMigration.ts